/**
 * Follow-cursor tooltip over `.bg-minimum-notice-disabled-slots`
 * background events. Mirrors the legacy
 * `@/pages/settings/SchedulingPreferences/MinimumNotice/MinimumNoticePopover`
 * but without the hard dependency on the `schedule-content-container`
 * DOM id — this one positions relative to its own wrapper so it drops
 * into any layout.
 *
 * Why: FullCalendar creates/destroys the greyed min-notice events as
 * the user scrolls and navigates, so we need a `MutationObserver` to
 * re-attach listeners rather than a one-shot effect.
 */
import { useEffect, useRef, useState } from 'react';
import { Box, Flex, Icon } from '@chakra-ui/react';
import { Lock01 } from '@kula-ai/icons/dual-tone/Lock01';
import { KText } from '@/ui-library/components';

export interface MinimumNoticeHoverPopoverProps {
  /** Hours of min-notice — renders as "X hour(s) minimum notice". */
  noticeHours: number;
  children: React.ReactNode;
}

function formatLabel(hours: number): string {
  if (hours <= 0) return '0 hours';
  if (hours % 24 === 0) {
    const days = hours / 24;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

export function MinimumNoticeHoverPopover({
  noticeHours,
  children,
}: MinimumNoticeHoverPopoverProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (noticeHours <= 0) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const onMove = (e: Event) => {
      const evt = e as MouseEvent;
      const rect = wrapper.getBoundingClientRect();
      setPos({
        top: evt.clientY - rect.top + 12,
        left: evt.clientX - rect.left + 12,
      });
    };
    const onLeave = () => setPos(null);

    const attach = () => {
      const nodes = wrapper.querySelectorAll(
        '.bg-minimum-notice-disabled-slots'
      );
      nodes.forEach((el) => {
        el.addEventListener('mousemove', onMove);
        el.addEventListener('mouseleave', onLeave);
      });
      return () => {
        nodes.forEach((el) => {
          el.removeEventListener('mousemove', onMove);
          el.removeEventListener('mouseleave', onLeave);
        });
      };
    };

    let detach = attach();
    // FullCalendar rebuilds BG events as the view/week changes — observe
    // and re-bind so the hover stays live across renders.
    const observer = new MutationObserver(() => {
      detach();
      detach = attach();
    });
    observer.observe(wrapper, { childList: true, subtree: true });

    return () => {
      detach();
      observer.disconnect();
    };
  }, [noticeHours]);

  return (
    <Box ref={wrapperRef} position="relative" w="100%" h="100%">
      {children}
      {pos && noticeHours > 0 ? (
        <Box
          position="absolute"
          top={`${pos.top}px`}
          left={`${pos.left}px`}
          bg="base-white"
          borderRadius="12px"
          boxShadow="0px 4px 16px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.04)"
          border="1px solid"
          borderColor="border-color"
          zIndex={999999}
          pointerEvents="none"
          width="280px"
          px="14px"
          py="14px"
        >
          <Flex gap="10px" alignItems="flex-start">
            <Box
              bg="pastel-4-light"
              borderRadius="6px"
              p="6px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              width="30px"
              height="30px"
            >
              <Icon
                as={Lock01}
                color="pastel-4-dark"
                width="18px"
                height="18px"
              />
            </Box>
            <Flex direction="column" gap="4px" flex="1" pt="1px">
              <KText color="secondary-900" size="sm-medium" lineHeight="20px">
                {formatLabel(noticeHours)} minimum notice
              </KText>
              <KText color="secondary-600" lineHeight="16px" size="sm">
                Select slots beyond this timeframe to give candidates options
              </KText>
            </Flex>
          </Flex>
        </Box>
      ) : null}
    </Box>
  );
}
